import { useEffect, useState } from "react";

import {
  addBlock,
  addDay,
  addExercise,
  duplicateBlock,
  duplicateDay,
  duplicateProgram,
  shareProgram,
  unshareProgram,
  type ShareLink,
  getProgram,
  removeBlock,
  removeDay,
  removeExercise,
  updateBlock,
  updateDay,
  updateExercise,
  updateProgram,
  type ExerciseDraft,
  type ProgramDetail,
  type ProgramExercise,
} from "../lib/api";
import { intensityClass, plural, targetLabel } from "../lib/format";
import { groupChained, plannedSets, restText } from "../lib/programPlan";
import { haptic, notify, shareToTelegram, useNativeBack } from "../lib/telegram";
import ConfirmButton from "../components/ConfirmButton";
import EditableText from "../components/EditableText";
import ExerciseEditor, { type ExercisePatch } from "../components/ExerciseEditor";
import ExerciseIcon from "../components/ExerciseIcon";
import Sheet from "../components/Sheet";
import { BusyLabel } from "../components/DotLoader";
import { useSession } from "../store/session";
import ExercisePicker from "./ExercisePicker";

interface Props {
  programId: number;
  onBack: () => void;
  onStartDay: (dayId: number) => void;
  /** Открыть другую программу — копию, созданную здесь. */
  onOpenProgram: (programId: number) => void;
}

/**
 * Упражнение дня — той же карточкой, что в тренировке (14.09.2026): заказчик
 * назвал прежний список строк «невзрачным» и хотел видеть день программы
 * сразу таким, каким он будет в зале. Отличие одно: вместо полей ввода — план
 * подходов, потому что это ещё не зал.
 */
function ProgramExerciseCard({ item, onOpen }: { item: ProgramExercise; onOpen: () => void }) {
  const [open, setOpen] = useState(false);
  const target = targetLabel(item.target_spec);
  let working = 0;
  return (
    <section className="wo-card">
      <div className="wo-card__head">
        <ExerciseIcon exercise={item.exercise} />
        {/* Вся шапка ведёт в правку: искать карандаш неудобно, а править здесь можно всё. */}
        <button className="wo-card__name" onClick={onOpen}>
          {item.exercise.name_ru}
          {item.is_backoff && <span className="wo-card__tag">добивка</span>}
        </button>
        <button
          className="wo-card__more"
          aria-label={`Изменить: ${item.exercise.name_ru}`}
          onClick={onOpen}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="12" cy="5.5" r="1.7" />
            <circle cx="12" cy="12" r="1.7" />
            <circle cx="12" cy="18.5" r="1.7" />
          </svg>
        </button>
      </div>

      {item.notes && <p className="wo-card__note">{item.notes}</p>}

      {(target || item.rest_sec) && (
        <p className="wo-card__rec">
          {target && <span className={intensityClass(item.target_spec)}>{target}</span>}
          {target && item.rest_sec ? " · " : ""}
          {item.rest_sec ? `отдых ${restText(item.rest_sec)}` : ""}
        </p>
      )}

      <div className="wo-sets wo-sets--static wo-sets--plan">
        <div className="wo-sets__head" aria-hidden="true">
          <span>Подх.</span>
          <span>Повт.</span>
          <span>Цель</span>
        </div>
        {plannedSets(item).map((set, i) => {
          if (set.number !== null) working += 1;
          return (
            <div key={i} className="wo-set">
              <span className="wo-set__no">
                {set.number === null ? <span className="wo-set__no--d">D</span> : working}
              </span>
              <span className="wo-set__val">{set.reps}</span>
              <span className="wo-set__val">{set.number === null ? "сброс" : target || "—"}</span>
            </div>
          );
        })}
      </div>

      {/* Слот методики: вариантов несколько, и выбор делается в зале. */}
      {item.alternatives.length > 0 && (
        <>
          <button
            className="wo-card__alts"
            aria-expanded={open}
            onClick={() => {
              haptic();
              setOpen((v) => !v);
            }}
          >
            {open ? "Свернуть варианты" : `+${item.alternatives.length} на выбор в зале`}
          </button>
          {open && (
            <p className="wo-card__alts-list">
              {item.alternatives.map((a) => a.exercise.name_ru).join(" · ")}
            </p>
          )}
        </>
      )}
    </section>
  );
}

export default function ProgramView({ programId, onBack, onStartDay, onOpenProgram }: Props) {
  const starting = useSession((s) => s.starting);
  const startError = useSession((s) => s.startError);
  const [program, setProgram] = useState<ProgramDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [blockIndex, setBlockIndex] = useState(0);
  // В какой день добавляем упражнение. null — шторка закрыта.
  const [pickerDayId, setPickerDayId] = useState<number | null>(null);
  // Какое упражнение правим: [dayId, строка].
  const [editing, setEditing] = useState<{ dayId: number; item: ProgramExercise } | null>(
    null,
  );
  const [blockSettings, setBlockSettings] = useState(false);
  // Ссылка «поделиться»: null — шторка закрыта.
  const [share, setShare] = useState<ShareLink | null>(null);
  const [copied, setCopied] = useState(false);
  const [working, setWorking] = useState(false);

  async function busy(action: () => Promise<void>) {
    if (working) return;
    setWorking(true);
    try {
      await action();
    } catch (e) {
      notify("error");
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setWorking(false);
    }
  }

  const openShare = () =>
    busy(async () => {
      haptic();
      setCopied(false);
      setShare(await shareProgram(programId));
    });

  const copyProgram = () =>
    busy(async () => {
      haptic("medium");
      const copy = await duplicateProgram(programId);
      notify("success");
      onOpenProgram(copy.id);
    });

  async function edit(action: () => Promise<ProgramDetail>) {
    try {
      setProgram(await action());
      setError(null);
    } catch (e) {
      notify("error");
      setError(e instanceof Error ? e.message : String(e));
      throw e;
    }
  }

  async function createDay() {
    haptic();
    // Именно открытый блок, а не последний: иначе тренировка уезжает в чужой.
    const current = program?.blocks[Math.min(blockIndex, (program?.blocks.length ?? 1) - 1)];
    const index = (current?.days.length ?? 0) + 1;
    await edit(() => addDay(programId, `Тренировка ${index}`, current?.id));
  }

  async function pickExercise(draft: ExerciseDraft) {
    const dayId = pickerDayId;
    setPickerDayId(null);
    if (dayId !== null) await edit(() => addExercise(programId, dayId, draft));
  }

  async function saveExercise(dayId: number, rowId: number, patch: ExercisePatch) {
    await edit(() => updateExercise(programId, dayId, rowId, patch));
  }

  useEffect(() => {
    getProgram(programId)
      .then(setProgram)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [programId]);

  // Кнопка «назад» — нативная, в шапке Telegram. Свою стрелку не рисуем.
  useEffect(() => useNativeBack(onBack), [onBack]);

  if (error && !program) {
    return (
      <div className="screen">
        <p className="lead">Не удалось открыть программу: {error}</p>
      </div>
    );
  }

  if (!program) {
    return (
      <div className="screen">
        <div className="skeleton skeleton--title" />
        <div className="skeleton skeleton--card" />
      </div>
    );
  }

  // После удаления блока прежний индекс может указывать за край списка.
  const safeIndex = Math.min(blockIndex, Math.max(0, program.blocks.length - 1));
  const block = program.blocks[safeIndex];
  const exerciseCount = block?.days.reduce((n, d) => n + d.exercises.length, 0) ?? 0;

  return (
    <div className="screen">
      <div className="header">
        <span className="eyebrow">программа</span>
        <span className="eyebrow">
          {plural(program.blocks.length, "блок", "блока", "блоков")}
        </span>
      </div>

      {/* Править можно любую программу, включая импортированную и собранную
          генератором: заказчик правит их руками в таблицах и здесь ждёт того же.
          Структуру источник больше не защищает — она всё равно уже в базе. */}
      <EditableText
        className="title"
        ariaLabel="Название программы"
        value={program.name}
        onSave={(name) => edit(() => updateProgram(programId, { name })).then(() => {})}
      />

      {program.blocks.length > 1 && (
        // Горизонтальная лента вместо сетки кнопок: блоков бывает 13, рядами
        // они съедят пол-экрана. Прокрутка — привычный мобильный жест.
        <div className="chips" role="tablist" aria-label="Блоки программы">
          {program.blocks.map((b, i) => (
            <button
              key={b.id}
              role="tab"
              aria-selected={i === safeIndex}
              className={`chip ${i === safeIndex ? "chip--active" : ""}`}
              onClick={() => {
                haptic();
                setBlockIndex(i);
                setBlockSettings(false);
              }}
            >
              {b.name}
            </button>
          ))}
        </div>
      )}

      {/* Действия с программой целиком — лентой под названием, а не рядом кнопок. */}
      <div className="pactions">
        <button className="chip" disabled={working} onClick={() => void openShare()}>
          Поделиться
        </button>
        <button className="chip" disabled={working} onClick={() => void copyProgram()}>
          Копия программы
        </button>
        <button
          className="chip"
          onClick={() => {
            haptic();
            const next = program.blocks.length + 1;
            void edit(() => addBlock(programId, `Блок ${next}`)).then(() =>
              setBlockIndex(program.blocks.length),
            );
          }}
        >
          + Блок
        </button>
      </div>

      {block && (
        <>
          <div className="block-head">
            <EditableText
              className="block-head__name"
              ariaLabel="Название блока"
              value={block.name}
              onSave={(name) =>
                edit(() => updateBlock(programId, block.id, { name })).then(() => {})
              }
            />
            <button
              className="link-button"
              aria-expanded={blockSettings}
              onClick={() => {
                haptic();
                setBlockSettings((v) => !v);
              }}
            >
              {blockSettings ? "свернуть" : "настроить"}
            </button>
          </div>

          <p className="block-meta">
            {plural(block.days.length, "тренировка", "тренировки", "тренировок")} ·{" "}
            {plural(exerciseCount, "упражнение", "упражнения", "упражнений")}
            {block.repeat_weeks > 1 &&
              ` · повторяется ${plural(block.repeat_weeks, "неделю", "недели", "недель")}`}
            {block.is_deload && " · разгрузка"}
          </p>

          {blockSettings && (
            <div className="stack">
              <label className="field">
                <span className="field__label">Повторять недель</span>
                <span className="field__control">
                  <input
                    className="field__input"
                    inputMode="numeric"
                    defaultValue={block.repeat_weeks}
                    onBlur={(e) => {
                      const weeks = Number(e.target.value);
                      if (weeks >= 1 && weeks !== block.repeat_weeks) {
                        void edit(() =>
                          updateBlock(programId, block.id, { repeat_weeks: weeks }),
                        );
                      }
                    }}
                  />
                </span>
              </label>
              <button
                className={`chip ${block.is_deload ? "chip--active" : ""}`}
                onClick={() => {
                  haptic();
                  void edit(() =>
                    updateBlock(programId, block.id, { is_deload: !block.is_deload }),
                  );
                }}
              >
                Разгрузочный блок
              </button>
              <button
                className="wo-btn"
                onClick={() => {
                  haptic();
                  const next = program.blocks.length;
                  void edit(() => duplicateBlock(programId, block.id)).then(() => {
                    setBlockIndex(next);
                    setBlockSettings(false);
                  });
                }}
              >
                Дублировать блок
              </button>
              {/* Блок уносит с собой все свои тренировки, поэтому вторым тапом. */}
              <ConfirmButton
                className="danger-button"
                label={`Удалить «${block.name}»`}
                confirm={`Точно? Уйдут ${plural(block.days.length, "тренировка", "тренировки", "тренировок")}`}
                onConfirm={() => {
                  setBlockIndex(Math.max(0, safeIndex - 1));
                  void edit(() => removeBlock(programId, block.id));
                }}
              />
            </div>
          )}

          {error && <p className="lead lead--error">{error}</p>}

          <div className="pdays">
            {block.days.map((day) => {
              const sets = day.exercises.reduce((n, e) => n + plannedSets(e).length, 0);
              return (
                <section key={day.id} className="pday">
                  <div className="pday__head">
                    <div className="pday__title">
                      <EditableText
                        className="pday__name"
                        ariaLabel="Название тренировки"
                        value={day.name}
                        onSave={(name) =>
                          edit(() => updateDay(programId, day.id, { name })).then(() => {})
                        }
                      />
                      <p className="pday__meta">
                        {plural(day.exercises.length, "упражнение", "упражнения", "упражнений")}
                        {sets > 0 && ` · ${plural(sets, "подход", "подхода", "подходов")}`}
                      </p>
                    </div>
                    <button
                      className="icon-button"
                      aria-label={`Копировать ${day.name}`}
                      onClick={() => {
                        haptic();
                        void edit(() => duplicateDay(programId, day.id));
                      }}
                    >
                      <svg viewBox="0 0 24 24" aria-hidden="true" className="icon-button__svg">
                        <rect x="8" y="8" width="12" height="12" rx="2.5" />
                        <path d="M16 8V6.5A2.5 2.5 0 0 0 13.5 4h-7A2.5 2.5 0 0 0 4 6.5v7A2.5 2.5 0 0 0 6.5 16H8" />
                      </svg>
                    </button>
                    <ConfirmButton
                      className="icon-button"
                      ariaLabel={`Удалить ${day.name}`}
                      label="×"
                      confirm="удалить?"
                      onConfirm={() => void edit(() => removeDay(programId, day.id))}
                    />
                  </div>

                  {day.exercises.length === 0 ? (
                    <p className="pday__empty">Пока пусто — добавьте первое упражнение.</p>
                  ) : (
                    <div className="session__list">
                      {groupChained(day.exercises).map((group, gi) => (
                        <div
                          key={gi}
                          className={`exgroup ${group.length > 1 ? "exgroup--chained" : ""}`}
                        >
                          {group.map((e) => (
                            <ProgramExerciseCard
                              key={e.id}
                              item={e}
                              onOpen={() => {
                                haptic();
                                setEditing({ dayId: day.id, item: e });
                              }}
                            />
                          ))}
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="pday__tail">
                    <button
                      className="wo-btn"
                      onClick={() => {
                        haptic();
                        setPickerDayId(day.id);
                      }}
                    >
                      + Упражнение
                    </button>
                    {/* Без этой кнопки программу нельзя было запустить вообще —
                        только через отдельную вкладку, что заказчик и не нашёл. */}
                    <button
                      className="button button--wide"
                      disabled={day.exercises.length === 0 || starting !== null}
                      onClick={() => {
                        haptic("medium");
                        onStartDay(day.id);
                      }}
                    >
                      {starting === day.id ? <BusyLabel text="Начинаем" /> : "Начать тренировку"}
                    </button>
                    {startError?.day === day.id && (
                      <p className="lead lead--error" role="alert" style={{ margin: 0 }}>
                        {startError.message}
                      </p>
                    )}
                  </div>
                </section>
              );
            })}
          </div>

          <button className="wo-btn pdays__add" onClick={createDay}>
            + Новая тренировка
          </button>
        </>
      )}

      {program.blocks.length === 0 && (
        <div className="empty">
          <p className="empty__title">Пустая программа</p>
          <p className="lead">Добавь первую тренировку — дальше наполнишь упражнениями.</p>
          <button className="button" onClick={createDay}>
            + Тренировка
          </button>
        </div>
      )}

      {pickerDayId !== null && (
        <ExercisePicker onPick={pickExercise} onClose={() => setPickerDayId(null)} />
      )}

      {editing && (
        <ExerciseEditor
          item={editing.item}
          onClose={() => setEditing(null)}
          onSave={(patch) => saveExercise(editing.dayId, editing.item.id, patch)}
          onDelete={() =>
            edit(() => removeExercise(programId, editing.dayId, editing.item.id)).then(
              () => {},
            )
          }
        />
      )}

      {share && (
        <Sheet title="Поделиться программой" onClose={() => setShare(null)}>
          <p className="lead" style={{ margin: 0 }}>
            Кто откроет ссылку, увидит программу и добавит её себе копией. Ваши
            правки к нему не попадут.
          </p>
          <p className="share__link">{share.url}</p>
          <button
            className="button button--wide"
            onClick={() => {
              haptic("medium");
              const text = `Программа «${program.name}» в RACK`;
              if (!shareToTelegram(share.url, text)) {
                void navigator.clipboard?.writeText(share.url).then(() => setCopied(true));
              }
            }}
          >
            Отправить в Telegram
          </button>
          <button
            className="wo-btn"
            onClick={() => {
              haptic();
              void navigator.clipboard?.writeText(share.url).then(() => {
                notify("success");
                setCopied(true);
              });
            }}
          >
            {copied ? "Скопировано" : "Скопировать ссылку"}
          </button>
          <ConfirmButton
            className="danger-button"
            label="Отозвать ссылку"
            confirm="Точно? Старая ссылка перестанет открываться"
            onConfirm={() =>
              void busy(async () => {
                await unshareProgram(programId);
                notify("success");
                setShare(null);
              })
            }
          />
        </Sheet>
      )}
    </div>
  );
}
